import * as stylex from '@stylexjs/stylex';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox } from '@/core/Checkbox.atom';
import { EditableText } from '@/core/EditableText.atom';
import { IconSelect } from '@/core/IconSelect.molecule';
import { FormulaEditorAsync } from '@/formula/FormulaEditorAsync.molecule';
import { formulaTableStyles as styles } from '@/tools/formula/formulaTable.styles';
import { OutputItemMetadataLabel } from '@/tools/models/OutputItemMetadataLabel.atom';
import { templateStyles } from '@/tools/models/template.styles';
import {
  faAngleDown,
  faChevronRight,
  faCircleCheck,
  faCircleInfo,
  faPencil,
  faQuestionCircle,
  faTrash,
} from '@seeqdev/icons/fa-sharp-regular-svg-icons';
import { fcFormula } from '@seeqdev/icons/seeq';
import { Badge, Icon } from '@seeqdev/qomponents';
import type { FeatureMappingConfig, FormulaItemTableOption } from '@/tools/formula/FormulaItemTable.molecule';
import type { OutputBadges } from '@/tools/models/templateOutputStatus.utilities';
import type { IconDefinition } from '@seeqdev/icons';

/**
 * A row is treated as a template derived feature (and therefore opted in to the metadata annotation)
 * when the option object carries the metadata keys — even with undefined values. Template display
 * utilities always project these keys onto every option they produce; non-template callers don't, so
 * their rows render without the annotation.
 */
const isTemplateDerivedFeatureOption = (option: FormulaItemTableOption): boolean =>
  'outputItemType' in option || 'outputItemMetadata' in option;

export interface FormulaItemTableRowProps {
  option: FormulaItemTableOption;
  testId?: string;
  isOpen: boolean;
  onToggleFormula: () => void;
  onEditClick?: (key: string) => void;
  onRemoveClick?: (key: string) => void;
  onAdditionalIconClick?: (key: string) => void;
  additionalIcon?: IconDefinition;
  additionalIconTooltip?: string;
  onSecondaryIconClick?: (key: string) => void;
  secondaryIcon?: string;
  secondaryIconTooltip?: string;
  textEditable?: boolean;
  onTextChange?: (newValue: string, original: FormulaItemTableOption) => void;
  newLabel?: string;
  mappingConfig?: FeatureMappingConfig;
  selectable?: boolean;
  isSelected?: boolean;
  onSelectionChange?: (key: string, selected: boolean) => void;
  badges?: OutputBadges;
}

export const FormulaItemTableRow = ({
  option,
  testId,
  isOpen,
  onToggleFormula,
  onEditClick,
  onRemoveClick,
  onAdditionalIconClick,
  additionalIcon,
  additionalIconTooltip,
  onSecondaryIconClick,
  secondaryIcon,
  secondaryIconTooltip,
  textEditable,
  onTextChange,
  newLabel,
  mappingConfig,
  selectable = false,
  isSelected = false,
  onSelectionChange,
  badges,
}: FormulaItemTableRowProps) => {
  const { t } = useTranslation();
  const [badgesCollapsed, setBadgesCollapsed] = useState(false);

  const isUnselected = selectable && !isSelected;
  const showNewLabel = !!newLabel && (option.isNew || option.showNewLabel);

  // Badges arrive already grouped into the lines they render as: one group for an output computed by a
  // single formula, one per formula when it has alternatives. Nothing here has to work out which is which.
  const badgeGroups = isUnselected ? [] : (badges?.groups ?? []);
  const helperText = isUnselected ? undefined : badges?.helperText;
  // Only a grouped set needs collapsing, and only a grouped set gets helper text to collapse it with.
  const isCollapsible = badgeGroups.length > 1 && !!helperText;

  let badgeIndex = 0;
  const renderedBadgeLines = badgeGroups.map((group, groupIndex) => (
    <div key={groupIndex} {...stylex.props(styles.badgeLine)}>
      {group.map((badge) => {
        const index = badgeIndex++;
        const element = (
          <Badge variant={badge.variant} label={badge.label}>
            {badge.text}
          </Badge>
        );
        if (!badge.tooltip) {
          return <span key={index}>{element}</span>;
        }
        return (
          <span
            key={index}
            data-qtip-text={badge.tooltip}
            data-qtip-testid={`${testId}BadgeTooltip-${option.key}-${index}`}>
            {element}
          </span>
        );
      })}
    </div>
  ));

  // The helper text doubles as the section's toggle: it explains the groups, so it is also what hides them.
  const badgeRow = badgeGroups.length > 0 && (
    <div {...stylex.props(styles.badgeSummary)}>
      {helperText ? (
        <button
          type="button"
          {...stylex.props(styles.badgeToggle)}
          onClick={() => setBadgesCollapsed(!badgesCollapsed)}
          aria-expanded={!badgesCollapsed}
          data-testid={`${testId}HelperText-${option.key}`}>
          <Icon icon={badges?.isComplete ? faCircleCheck : faCircleInfo} type="inherit" />
          {helperText}
          <Icon icon={badgesCollapsed ? faChevronRight : faAngleDown} type="inherit" />
        </button>
      ) : null}
      {!isCollapsible || !badgesCollapsed ? renderedBadgeLines : null}
    </div>
  );

  return (
    <tr {...stylex.props(templateStyles.outputSection, styles.row, isUnselected && styles.unselected)}>
      {selectable && (
        <td {...stylex.props(styles.cell)}>
          <div {...stylex.props(styles.checkboxCell)}>
            <Checkbox
              id={`${testId ?? 'formulaItemTable'}Checkbox-${option.key}`}
              isChecked={isSelected}
              onChange={(e) => onSelectionChange?.(option.key, e.target.checked)}
            />
          </div>
        </td>
      )}
      <td colSpan={1} {...stylex.props(styles.cell)}>
        <div {...stylex.props(styles.rowContent, !selectable && styles.unselectableContent)}>
          <div {...stylex.props(styles.rowHeading)}>
            <div {...stylex.props(styles.nameColumn)}>
              {!textEditable && (
                <span>
                  <span {...stylex.props(styles.nameLabel)}>{t('NAME')}:</span> {option.display}
                  {showNewLabel ? <Badge xstyle={styles.newBadge}>{newLabel}</Badge> : null}
                </span>
              )}
              {textEditable && onTextChange && (
                <span {...stylex.props(styles.editableName)}>
                  <span {...stylex.props(styles.nameLabel)}>{t('NAME')}:</span>
                  <EditableText
                    value={option.display}
                    onUpdate={(newText) => onTextChange(newText, option)}
                    textClasses={stylex.props(styles.inlineName).className}
                  />
                  {showNewLabel ? <Badge xstyle={styles.newBadge}>{newLabel}</Badge> : null}
                </span>
              )}
              {isTemplateDerivedFeatureOption(option) && (
                <OutputItemMetadataLabel
                  outputItemType={option.outputItemType}
                  outputItemMetadata={option.outputItemMetadata}
                />
              )}
              {badgeRow}
            </div>
            <div {...stylex.props(styles.rowActions)}>
              {option.allFormulas && (
                <Icon icon={fcFormula} onClick={onToggleFormula} testId={`${testId}FormulaIcon-${option.key}`} />
              )}
              {onEditClick && (
                <Icon
                  icon={faPencil}
                  onClick={() => onEditClick(option.key)}
                  testId={`${testId}EditIcon-${option.key}`}
                  xstyle={styles.actionIcon}
                />
              )}
              {onAdditionalIconClick && additionalIcon && (
                <Icon
                  icon={additionalIcon}
                  onClick={() => onAdditionalIconClick(option.key)}
                  testId={`${testId}ExtraIcon-${option.key}`}
                  xstyle={styles.actionIcon}
                  tooltip={additionalIconTooltip && t(additionalIconTooltip)}
                />
              )}
              {onSecondaryIconClick && secondaryIcon && (
                <Icon
                  icon={secondaryIcon}
                  onClick={() => onSecondaryIconClick(option.key)}
                  testId={`${testId}SecondaryExtraIcon-${option.key}`}
                  xstyle={styles.actionIcon}
                  tooltip={secondaryIconTooltip && t(secondaryIconTooltip)}
                />
              )}
              {onRemoveClick && (
                <Icon
                  icon={faTrash}
                  onClick={() => onRemoveClick(option.key)}
                  testId={`${testId}RemoveIcon-${option.key}`}
                  xstyle={styles.actionIcon}
                />
              )}
            </div>
          </div>
          {option.isNew && mappingConfig && mappingConfig.mappingOptions.length > 0 && (
            <div {...stylex.props(styles.mappingCheckbox)}>
              <Checkbox
                isChecked={option.isMapped ?? false}
                onChange={(e) => mappingConfig.onMappingChange?.(option.key, e.target.checked, undefined)}
                label={t('TEMPLATES.MAP_TO_EXISTING_CHECKBOX_LABEL')}
                id={`${option.key}-mapping-checkbox`}
              />
            </div>
          )}
          {option.isNew && option.isMapped && mappingConfig && mappingConfig.mappingOptions.length > 0 && (
            <div {...stylex.props(styles.mappingControl)}>
              {mappingConfig && mappingConfig.label && mappingConfig.labelTooltip && (
                <div {...stylex.props(styles.mappingLabel)} data-testid={`${testId}MappingLabel`}>
                  <span {...stylex.props(styles.mappingLabelText)}>{t(mappingConfig?.label)}</span>
                  <Icon
                    icon={faQuestionCircle}
                    large={true}
                    tooltip={t(mappingConfig?.labelTooltip)}
                    testId={`${testId}MappingLabelIcon`}
                    tooltipPlacement="right"
                    xstyle={styles.mappingHelp}
                  />
                </div>
              )}
              <IconSelect
                testId={`${testId}MappingSelect-${option.key}`}
                selectOptions={mappingConfig?.mappingOptions}
                placeholder={mappingConfig?.mappingPlaceholder}
                value={option.mappedFeatureId}
                onChange={(selected) => mappingConfig.onMappingChange?.(option.key, true, selected.value as string)}
              />
            </div>
          )}
          {isOpen && option.allFormulas && (
            <div>
              {option.allFormulas.map((formula, idx) => (
                <div
                  key={idx}
                  {...stylex.props(
                    idx === 0 && styles.firstFormula,
                    idx !== option.allFormulas!.length - 1 && styles.formulaAlternative,
                  )}>
                  <span {...stylex.props(styles.nameLabel)}>
                    {t(
                      option.allFormulas!.length > 1 ? 'FORMULA.FORMULA_COUNT' : 'FORMULA.FORMULA',
                      option.allFormulas!.length > 1 ? { count: idx + 1 } : undefined,
                    )}
                    :
                  </span>{' '}
                  <FormulaEditorAsync
                    formula={formula.formula}
                    readOnly={true}
                    readOnlyWithErrors={true}
                    showLineNumbers={false}
                    lineWrapping={true}
                    formulaErrors={formula.formulaErrors}
                  />
                  {formula.parameters && formula.parameters.length > 0 && (
                    <div>
                      <span {...stylex.props(styles.parameterLabel)}>{t('FORMULA.DOCUMENTATION.PARAMETERS')}: </span>{' '}
                      {formula.parameters.map((param) => `${param.identifier} (${param.name})`).join(', ')}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </td>
    </tr>
  );
};
